// frontend/src/components/WhatsAppTemplateLibraryModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-16
//
// Description:
//   Modal for browsing and selecting WhatsApp templates from the local
//   whatsapp_templates table, with a "Sync from Twilio" action that calls
//   /api/messaging/templates/whatsapp/sync to pull fresh templates from
//   Twilio Content API.
//
// Responsive pass (2026-09-16):
//   • Mobile uses a viewport-safe near-full-height modal.
//   • Controls and footer actions stack cleanly on narrow screens.
//   • Desktop keeps the existing five-column template table.
//   • Mobile renders one complete template per selectable card instead of a compressed table.
//   • Selected templates use the configured accent background with readable white text.
//   • No template fetch, sync, selection or confirmation behaviour changed.
//
//   Usage:
//     <WhatsAppTemplateLibraryModal
//        isOpen={isOpen}
//        onClose={handleClose}
//        onSelectTemplate={(template) => { ... }}
//     />

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const WhatsAppTemplateLibraryModal = ({
  isOpen,
  onClose,
  onSelectTemplate,
  tenantId = 1,
}) => {
  const theme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const accentColor = theme?.accent_color || "#ff7a00";

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get("/api/messaging/templates/whatsapp", {
        params: { tenant_id: tenantId, include_inactive: false },
      });
      setTemplates(resp.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[WhatsAppTemplateLibraryModal] fetchTemplates error", err);
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const resp = await api.post(
        "/api/messaging/templates/whatsapp/sync",
        null,
        { params: { tenant_id: tenantId } }
      );
      // eslint-disable-next-line no-console
      console.log("[WhatsAppTemplateLibraryModal] sync result", resp.data);
      await fetchTemplates();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[WhatsAppTemplateLibraryModal] sync error", err);
      setError("Failed to sync templates from Twilio.");
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirm = () => {
    const template = templates.find((t) => t.id === selectedId);
    if (template && onSelectTemplate) onSelectTemplate(template);
    if (onClose) onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedId(null);
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ESC close
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId]
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/60 p-2 sm:p-4 lg:p-6"
      style={{
        zIndex: 260000, // must be above WhatsApp overlay (150000)
      }}
      onMouseDown={onClose}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="flex h-[calc(100dvh-16px)] w-full max-w-[1200px] flex-col overflow-hidden rounded-xl bg-white sm:h-[calc(100dvh-32px)] md:h-auto md:max-h-[90vh] md:rounded-2xl"
        style={{
          boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-gray-200 px-3 py-3 sm:items-center sm:px-[18px] sm:py-4">
          <div className="min-w-0 flex-1">
            <h2
              className="m-0 break-words text-lg font-semibold sm:text-xl"
              style={{ color: accentColor }}
            >
              WhatsApp Template Library
            </h2>
            <div className="mt-1 text-xs leading-5 text-gray-600">
              Templates are synced from Twilio Content API and stored locally per
              tenant.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-base hover:bg-gray-200"
            style={{
              border: "none",
              cursor: "pointer",
              opacity: 0.9,
            }}
            title="Close"
            aria-label="Close WhatsApp template library"
          >
            ✕
          </button>
        </div>

        {/* Controls */}
        <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 px-3 py-3 sm:px-[18px] md:flex-row md:items-center md:gap-3 md:py-[14px]">
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="w-full bg-btnActionNormal text-sm text-white hover:bg-btnActionHover active:bg-btnActionClick md:w-auto"
            style={{
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              cursor: syncing ? "default" : "pointer",
              whiteSpace: "nowrap",
              opacity: syncing ? 0.7 : 1,
            }}
          >
            {syncing ? "Syncing…" : "Sync from Twilio"}
          </button>

          {selectedTemplate && (
            <div
              className="min-w-0 break-words text-xs md:max-w-[65%]"
              style={{
                color: "#333",
                padding: "6px 10px",
                borderRadius: 10,
                background: "rgba(255,122,0,0.10)",
              }}
              title={selectedTemplate.name}
            >
              Selected:{" "}
              <span style={{ fontWeight: 700 }}>{selectedTemplate.name}</span>
            </div>
          )}

          <div className="hidden md:block md:flex-1" />
        </div>

        {/* Error */}
        {error && (
          <div
            className="mx-3 mt-3 shrink-0 break-words sm:mx-[18px]"
            style={{
              padding: 10,
              borderRadius: 10,
              background: "#ffe5e5",
              color: "#a00",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* Template content */}
        <div className="min-h-0 flex-1 overflow-auto px-3 pb-3 pt-3 sm:px-[18px] sm:pb-4">
          {loading ? (
            <div style={{ padding: 16, fontSize: 14 }}>Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="break-words" style={{ padding: 16, fontSize: 14 }}>
              No WhatsApp templates found. Click “Sync from Twilio” to import
              approved templates from your Twilio account.
            </div>
          ) : (
            <>
              {/* Mobile: one complete template per card */}
              <div className="space-y-3 md:hidden">
                {templates.map((t) => {
                  const isSelected = t.id === selectedId;
                  const vars = Array.isArray(t.variables_schema)
                    ? t.variables_schema
                    : [];

                  const varsText =
                    vars.length === 0
                      ? "-"
                      : vars
                          .map((v) => v?.label || v?.key || v?.name || "")
                          .filter(Boolean)
                          .slice(0, 6)
                          .join(", ");

                  const primaryText = isSelected ? "#ffffff" : "#111827";
                  const secondaryText = isSelected
                    ? "rgba(255,255,255,0.86)"
                    : "#4b5563";
                  const mutedText = isSelected
                    ? "rgba(255,255,255,0.74)"
                    : "#6b7280";

                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      className="block w-full overflow-hidden rounded-xl border text-left shadow-sm"
                      style={{
                        borderColor: isSelected ? accentColor : "#e5e7eb",
                        background: isSelected ? accentColor : "#ffffff",
                        color: primaryText,
                        padding: 0,
                      }}
                      title={t.body_preview || t.name}
                      aria-pressed={isSelected}
                    >
                      <div
                        className="flex items-start justify-between gap-3 px-3 py-3"
                        style={{
                          borderBottom: isSelected
                            ? "1px solid rgba(255,255,255,0.22)"
                            : "1px solid #f0f0f0",
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <div
                            className="break-words text-sm font-bold"
                            style={{ color: primaryText }}
                          >
                            {t.name}
                          </div>

                          {t.body_preview && (
                            <div
                              className="mt-1 whitespace-normal break-words text-xs leading-5"
                              style={{ color: secondaryText }}
                            >
                              {t.body_preview}
                            </div>
                          )}
                        </div>

                        <span
                          className="shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                          style={{
                            background: isSelected
                              ? "rgba(255,255,255,0.18)"
                              : "rgba(17,24,39,0.06)",
                            color: isSelected ? "#ffffff" : "#374151",
                          }}
                        >
                          {isSelected ? "Selected" : "Select"}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 px-3 py-3 text-xs">
                        <div className="min-w-0">
                          <div
                            className="mb-1 text-[10px] font-bold uppercase tracking-wide"
                            style={{ color: mutedText }}
                          >
                            Content SID
                          </div>
                          <code
                            className="block break-all text-[11px]"
                            style={{ color: primaryText }}
                          >
                            {t.content_sid || "-"}
                          </code>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="min-w-0">
                            <div
                              className="mb-1 text-[10px] font-bold uppercase tracking-wide"
                              style={{ color: mutedText }}
                            >
                              Language
                            </div>
                            <div className="break-words" style={{ color: primaryText }}>
                              {t.language || "-"}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div
                              className="mb-1 text-[10px] font-bold uppercase tracking-wide"
                              style={{ color: mutedText }}
                            >
                              Status
                            </div>
                            <div className="break-words" style={{ color: primaryText }}>
                              {t.provider_status || t.status || "-"}
                            </div>
                          </div>
                        </div>

                        <div className="min-w-0">
                          <div
                            className="mb-1 text-[10px] font-bold uppercase tracking-wide"
                            style={{ color: mutedText }}
                          >
                            Variables
                          </div>

                          <div className="break-words" style={{ color: primaryText }}>
                            {varsText || "-"}
                            {vars.length > 6 ? ` +${vars.length - 6}` : ""}
                          </div>

                          {vars.length > 0 && (
                            <div
                              className="mt-1 space-y-1 break-words text-[11px] leading-4"
                              style={{ color: secondaryText }}
                            >
                              {vars
                                .slice(0, 3)
                                .map((v, i2) => {
                                  const ex =
                                    v?.example ??
                                    v?.example_value ??
                                    v?.sample ??
                                    v?.default ??
                                    "";
                                  const key =
                                    v?.key ||
                                    v?.label ||
                                    v?.name ||
                                    `var${i2 + 1}`;
                                  if (!ex) return null;
                                  return (
                                    <div key={`${t.id}-mobile-ex-${i2}`}>
                                      <span style={{ fontWeight: 700 }}>
                                        {String(key)}:
                                      </span>{" "}
                                      {String(ex)}
                                    </div>
                                  );
                                })
                                .filter(Boolean)}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Desktop: retain the existing table presentation */}
              <table
                className="hidden md:table"
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#fafafa",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Name / Preview
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid #eee",
                        width: 220,
                      }}
                    >
                      Content SID
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid #eee",
                        width: 110,
                      }}
                    >
                      Lang
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid #eee",
                        width: 140,
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid #eee",
                        width: 260,
                      }}
                    >
                      Variables
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {templates.map((t, idx) => {
                    const isSelected = t.id === selectedId;
                    const vars = Array.isArray(t.variables_schema)
                      ? t.variables_schema
                      : [];

                    const zebra =
                      idx % 2 === 1 ? "#fff6ef" : "#fff"; // very light orange
                    const bg = isSelected ? accentColor : zebra;
                    const primaryText = isSelected ? "#ffffff" : "#111827";
                    const secondaryText = isSelected
                      ? "rgba(255,255,255,0.86)"
                      : "#555";
                    const mutedText = isSelected
                      ? "rgba(255,255,255,0.76)"
                      : "#666";

                    const varsText =
                      vars.length === 0
                        ? "-"
                        : vars
                            .map((v) => v?.label || v?.key || v?.name || "")
                            .filter(Boolean)
                            .slice(0, 6)
                            .join(", ");

                    return (
                      <tr
                        key={t.id}
                        onClick={() => setSelectedId(t.id)}
                        style={{
                          cursor: "pointer",
                          background: bg,
                          color: primaryText,
                        }}
                        title={t.body_preview || t.name}
                        aria-selected={isSelected}
                      >
                        <td
                          style={{
                            padding: "10px 12px",
                            borderBottom: isSelected
                              ? "1px solid rgba(255,255,255,0.18)"
                              : "1px solid #f0f0f0",
                          }}
                        >
                          <div style={{ fontWeight: 700, color: primaryText }}>
                            {t.name}
                          </div>

                          {t.body_preview && (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 12,
                                color: secondaryText,
                                whiteSpace: "normal",
                                lineHeight: 1.35,
                              }}
                            >
                              {t.body_preview}
                            </div>
                          )}
                        </td>

                        <td
                          style={{
                            padding: "10px 12px",
                            borderBottom: isSelected
                              ? "1px solid rgba(255,255,255,0.18)"
                              : "1px solid #f0f0f0",
                          }}
                        >
                          <code
                            style={{
                              fontSize: 11,
                              wordBreak: "break-all",
                              color: primaryText,
                            }}
                          >
                            {t.content_sid || "-"}
                          </code>
                        </td>

                        <td
                          style={{
                            padding: "10px 12px",
                            borderBottom: isSelected
                              ? "1px solid rgba(255,255,255,0.18)"
                              : "1px solid #f0f0f0",
                            color: primaryText,
                          }}
                        >
                          {t.language || "-"}
                        </td>

                        <td
                          style={{
                            padding: "10px 12px",
                            borderBottom: isSelected
                              ? "1px solid rgba(255,255,255,0.18)"
                              : "1px solid #f0f0f0",
                            color: primaryText,
                          }}
                        >
                          {t.provider_status || t.status || "-"}
                        </td>

                        <td
                          style={{
                            padding: "10px 12px",
                            borderBottom: isSelected
                              ? "1px solid rgba(255,255,255,0.18)"
                              : "1px solid #f0f0f0",
                          }}
                        >
                          <div style={{ color: primaryText }}>
                            {varsText || "-"}
                            {vars.length > 6 ? ` +${vars.length - 6}` : ""}
                          </div>

                          {/* Show examples if present (non-breaking improvement) */}
                          {vars.length > 0 && (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 11,
                                color: mutedText,
                                lineHeight: 1.3,
                              }}
                            >
                              {vars
                                .slice(0, 3)
                                .map((v, i2) => {
                                  const ex =
                                    v?.example ??
                                    v?.example_value ??
                                    v?.sample ??
                                    v?.default ??
                                    "";
                                  const key =
                                    v?.key ||
                                    v?.label ||
                                    v?.name ||
                                    `var${i2 + 1}`;
                                  if (!ex) return null;
                                  return (
                                    <div key={`${t.id}-ex-${i2}`}>
                                      <span style={{ fontWeight: 700 }}>
                                        {String(key)}:
                                      </span>{" "}
                                      {String(ex)}
                                    </div>
                                  );
                                })
                                .filter(Boolean)}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 bg-white px-3 py-3 sm:flex-row sm:justify-end sm:gap-[10px] sm:px-[18px]">
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
            style={{
              padding: "9px 16px",
              borderRadius: 10,
              border: "none",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedId}
            className="w-full text-white sm:w-auto"
            style={{
              padding: "9px 18px",
              borderRadius: 10,
              border: "none",
              cursor: selectedId ? "pointer" : "default",
              backgroundColor: selectedId ? accentColor : "#ccc",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Use Template
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WhatsAppTemplateLibraryModal;
